/**
 * Meeting dialogs beyond the editor (Outlook): propose a new time (attendee), forward a meeting and add attendees
 * to a sent meeting (organizer; only the new people get the invitation).
 */
import { useI18n } from '@platform/i18n';
import {
  Button,
  checkNames,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DateTimePicker,
  Label,
  needsAttention,
  RecipientInput,
  Textarea,
  toast,
  useErrorText,
  type Recipient,
} from '@platform/ui';
import { useId, useState, type FormEvent } from 'react';
import { api, type CalendarEvent } from '../api';
import { useRecipientLabels } from '../compose-labels';
import { useRecipientDirectory } from '../recipient-directory';
import { useCalendar } from './calendar-context';
import { useCalendarFormat } from './format';

const pad = (n: number) => String(n).padStart(2, '0');
const dateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeValue = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const combine = (date: string, time: string) => new Date(`${date}T${time || '00:00'}:00`);

export type MeetingDialog = { kind: 'propose' | 'forward' | 'addAttendees'; event: CalendarEvent; title: string };

export function MeetingDialogs({ dialog, onClose }: { dialog: MeetingDialog | undefined; onClose: () => void }) {
  if (!dialog) return null;
  return dialog.kind === 'propose' ? <ProposeDialog dialog={dialog} onClose={onClose} /> : <PeopleDialog dialog={dialog} onClose={onClose} />;
}

function ProposeDialog({ dialog, onClose }: { dialog: MeetingDialog; onClose: () => void }) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const errorText = useErrorText();
  const { changed, zone } = useCalendar();
  const ids = { date: useId(), start: useId(), end: useId(), comment: useId() };
  const original = { start: zone.toWall(dialog.event.start), end: zone.toWall(dialog.event.end) };
  const [date, setDate] = useState(dateValue(original.start));
  const [startTime, setStartTime] = useState(timeValue(original.start));
  const [endTime, setEndTime] = useState(timeValue(original.end));
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const start = combine(date, startTime);
  const end = combine(date, endTime);
  const valid = !Number.isNaN(start.getTime()) && end > start;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const range = format.range(start, end, false);
      const result = await api.proposeNewTime(dialog.event.id, {
        response: 'tentative',
        start: zone.fromWall(start).toISOString(),
        end: zone.fromWall(end).toISOString(),
        ...(comment.trim() && { comment: comment.trim() }),
        note: t('webmail.calendarUx.propose.note', { day: format.dayLabel(start), range }),
      });
      toast.success(t(result.delivery === 'proposed' ? 'webmail.calendarUx.propose.sentToast' : 'webmail.calendarUx.propose.noteToast'));
      changed();
      onClose();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('webmail.calendarUx.propose.title')}</DialogTitle>
          <DialogDescription>{t('webmail.calendarUx.propose.description', { title: dialog.title, range: format.range(original.start, original.end, false) })}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.date}>{t('webmail.calendarUx.propose.date')}</Label>
            <DateTimePicker id={ids.date} mode="date" weekStartsOn={format.weekStart} weekTimeZone={format.weekTimeZone} value={date} onValueChange={setDate} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.start}>{t('webmail.calendar.editor.startTime')}</Label>
              <DateTimePicker id={ids.start} mode="time" formatTime={format.time} step={300} value={startTime} onValueChange={setStartTime} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.end}>{t('webmail.calendar.editor.endTime')}</Label>
              <DateTimePicker id={ids.end} mode="time" formatTime={format.time} step={300} value={endTime} onValueChange={setEndTime} aria-invalid={!valid} />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.comment}>{t('webmail.calendarUx.rsvp.message')}</Label>
            <Textarea id={ids.comment} rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy || !valid}>
              {t('webmail.calendarUx.propose.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Forward (anyone in the meeting) or add attendees (organizer): pick people, optional message for forwarding. */
function PeopleDialog({ dialog, onClose }: { dialog: MeetingDialog; onClose: () => void }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { changed } = useCalendar();
  const directory = useRecipientDirectory();
  const { recipientLabels } = useRecipientLabels();
  const ids = { people: useId(), comment: useId() };
  const [people, setPeople] = useState<Recipient[]>([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const forward = dialog.kind === 'forward';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const checked = await checkNames(people, directory);
      setPeople(checked);
      if (checked.length === 0 || checked.some(needsAttention)) return;
      const added = checked.map((r) => ({ address: r.email, ...(r.name && { name: r.name }) }));
      if (forward) {
        await api.forwardMeeting(dialog.event.id, { recipients: added, ...(comment.trim() && { comment: comment.trim() }) });
        toast.success(t('webmail.calendarUx.people.forwardedToast', { n: added.length }));
      } else {
        const known = new Set(dialog.event.attendees.map((a) => a.address.toLowerCase()));
        const attendees = [
          ...dialog.event.attendees.map((a) => ({ address: a.address, name: a.name, type: a.type })),
          ...added.filter((a) => !known.has(a.address.toLowerCase())).map((a) => ({ ...a, type: 'required' as const })),
        ];
        // Asks for the update to the changed (new) attendees only; gromox sends it to everyone (gx-nf-ews-send-to-changed).
        await api.updateEvent(dialog.event.id, { attendees }, 'occurrence', true);
        toast.success(t('webmail.calendarUx.people.addedToast', { n: added.length }));
        changed();
      }
      onClose();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(forward ? 'webmail.calendarUx.people.forwardTitle' : 'webmail.calendarUx.people.addTitle')}</DialogTitle>
          <DialogDescription>{t(forward ? 'webmail.calendarUx.people.forwardDescription' : 'webmail.calendarUx.people.addDescription', { title: dialog.title })}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)}>
          <Label htmlFor={ids.people}>{t('webmail.calendarUx.people.people')}</Label>
          <RecipientInput id={ids.people} field="to" label={t('webmail.calendarUx.people.people')} value={people} onChange={setPeople} directory={directory} labels={recipientLabels} autoFocus />
          {forward && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.comment}>{t('webmail.calendarUx.rsvp.message')}</Label>
              <Textarea id={ids.comment} rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy || people.length === 0}>
              {t(forward ? 'webmail.calendarUx.people.forward' : 'webmail.calendarUx.people.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
